import { useState, useMemo, useCallback, useEffect } from "react";
import type { SortingState } from "@tanstack/react-table";
import type { SortField } from "../columnConfig";
import { COLUMN_TO_SORT_FIELD_MAP } from "../columnConfig";
import type { ColumnId } from "../columnConfig";

export type SortDirection = "asc" | "desc";

export interface UseStaffSortingOptions {
  initialSorting: SortingState;
  onSortingChange: (
    sorting: SortingState,
    resetPagination?: () => void
  ) => void;
}

export interface UseStaffSortingReturn {
  sorting: SortingState;
  sortField: SortField | null;
  sortDirection: SortDirection;
  setSorting: (
    updater: SortingState | ((prev: SortingState) => SortingState)
  ) => void;
}

export function useStaffSorting({
  initialSorting,
  onSortingChange,
}: UseStaffSortingOptions): UseStaffSortingReturn {
  const [sorting, setSortingState] = useState<SortingState>(initialSorting);

  useEffect(() => {
    setSortingState(initialSorting);
  }, [initialSorting]);

  const sortField = useMemo<SortField | null>(() => {
    if (sorting.length === 0) return null;
    const firstSort = sorting[0];
    const columnId = firstSort.id as ColumnId;

    const mappedSortField = COLUMN_TO_SORT_FIELD_MAP[columnId];
    return mappedSortField || null;
  }, [sorting]);

  const sortDirection = useMemo<SortDirection>(() => {
    if (sorting.length === 0) return "desc";
    return sorting[0].desc ? "desc" : "asc";
  }, [sorting]);

  const setSorting = useCallback(
    (updater: SortingState | ((prev: SortingState) => SortingState)) => {
      const newSorting =
        typeof updater === "function" ? updater(sorting) : updater;

      if (newSorting.length === 0 && sorting.length > 0) {
        const currentSort = sorting[0];
        const toggledSorting: SortingState = [
          { id: currentSort.id, desc: !currentSort.desc },
        ];
        setSortingState(toggledSorting);
        onSortingChange(toggledSorting);
      } else {
        setSortingState(newSorting);
        onSortingChange(newSorting);
      }
    },
    [sorting, onSortingChange]
  );

  return {
    sorting,
    sortField,
    sortDirection,
    setSorting,
  };
}
